import React, { useMemo, useState } from 'react';
import ReactDOM from 'react-dom';
import { FiMap, FiGrid, FiMenu, FiCheck, FiCalendar, FiHome, FiPhone, FiChevronDown } from 'react-icons/fi';
// No route changes for Open Houses vs All Listings; single-page toggle handled by App state
import { COLORS, FONT_STACK } from '../theme';
import AgentAvatar from '../AgentAvatar';
import ContactModal from './ContactModal'; // Import the modal
import analyticsService from '../services/analyticsService';
import { formatPhoneNumber } from '../utils/phoneUtils';

export default function AppHeader({ agent, viewMode, onViewModeChange, dataMode, onDataModeChange, isMobile = false, onGoHome }) {
  const [showContactModal, setShowContactModal] = useState(false);
  const [isDemoHovered, setIsDemoHovered] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [mobileMenuAnchor, setMobileMenuAnchor] = useState(null);
  const [isDesktopDataModeMenuOpen, setIsDesktopDataModeMenuOpen] = useState(false);
  const [isDesktopTitleHovered, setIsDesktopTitleHovered] = useState(false);
  const mobileMenuRef = React.useRef(null);
  const mobileMenuButtonRef = React.useRef(null);
  const desktopDataModeMenuRef = React.useRef(null);
  const desktopDataModeButtonRef = React.useRef(null);

  const headerTitle = useMemo(() => {
    if (dataMode === 'listings') return 'All Listings';
    return 'Open Houses';
  }, [dataMode]);

  const canSwitchDataMode = useMemo(() => {
    // Demo mode intentionally has no data-mode switching.
    if (dataMode === 'demo') return false;
    return typeof onDataModeChange === 'function';
  }, [dataMode, onDataModeChange]);

  const handleDataModeNavigate = (nextDataMode) => {
    if (!nextDataMode || nextDataMode === dataMode) return;

    analyticsService.trackEvent({
      category: 'Navigation',
      action: 'Data Mode Change',
      label: nextDataMode,
      value: 1
    });

    if (typeof onDataModeChange === 'function') {
      onDataModeChange(nextDataMode);
    }
  };

  // Close the desktop data-mode menu on outside click / escape
  React.useEffect(() => {
    if (!isDesktopDataModeMenuOpen) return;

    const onKeyDown = (e) => {
      if (e.key === 'Escape') setIsDesktopDataModeMenuOpen(false);
    };

    const onPointerDown = (e) => {
      const menuEl = desktopDataModeMenuRef.current;
      const btnEl = desktopDataModeButtonRef.current;
      if (!menuEl) return;
      if (menuEl.contains(e.target)) return;
      if (btnEl && btnEl.contains(e.target)) return;
      setIsDesktopDataModeMenuOpen(false);
    };

    document.addEventListener('keydown', onKeyDown);
    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('touchstart', onPointerDown, { passive: true });
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('touchstart', onPointerDown);
    };
  }, [isDesktopDataModeMenuOpen]);

  const openContactModalFromMobileHeader = (source) => {
    analyticsService.trackEvent({
      category: 'Contact',
      action: 'Open Contact Modal',
      label: source || 'Mobile Header',
      value: 1
    });
    setShowContactModal(true);
  };

  // Close the mobile menu on outside click / escape
  React.useEffect(() => {
    if (!isMobileMenuOpen) return;

    const onKeyDown = (e) => {
      if (e.key === 'Escape') setIsMobileMenuOpen(false);
    };

    const onPointerDown = (e) => {
      const menuEl = mobileMenuRef.current;
      const btnEl = mobileMenuButtonRef.current;
      if (!menuEl) return;
      if (menuEl.contains(e.target)) return;
      if (btnEl && btnEl.contains(e.target)) return;
      setIsMobileMenuOpen(false);
    };

    document.addEventListener('keydown', onKeyDown);
    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('touchstart', onPointerDown, { passive: true });
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('touchstart', onPointerDown);
    };
  }, [isMobileMenuOpen]);

  // When the mobile menu is open, compute an anchor position in viewport coordinates.
  // Render the dropdown in a portal so it stacks above Mapbox (which uses high z-index controls).
  React.useEffect(() => {
    if (!isMobileMenuOpen) {
      setMobileMenuAnchor(null);
      return;
    }

    const computeAnchor = () => {
      const btnEl = mobileMenuButtonRef.current;
      if (!btnEl || typeof btnEl.getBoundingClientRect !== 'function') return;
      const rect = btnEl.getBoundingClientRect();
      setMobileMenuAnchor({
        top: rect.bottom + 10,
        right: Math.max(8, window.innerWidth - rect.right),
        maxHeight: Math.max(240, Math.floor(window.innerHeight * 0.7)),
      });
    };

    computeAnchor();
    window.addEventListener('resize', computeAnchor);
    // Use capture on scroll so it updates even when scrolling inside nested containers.
    window.addEventListener('scroll', computeAnchor, true);
    return () => {
      window.removeEventListener('resize', computeAnchor);
      window.removeEventListener('scroll', computeAnchor, true);
    };
  }, [isMobileMenuOpen]);

  // Define mobile header JSX
  const mobileHeader = (
  <header style={{
      background: COLORS.white,
      borderBottom: `2px solid ${COLORS.primaryRed}`,
      // Vertical padding only; horizontal padding handled by inner container for alignment
      padding: '0.75rem 0',
      position: 'sticky',
      top: 0,
      zIndex: 1000
    }}>
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        boxSizing: 'border-box',
        width: '100%',
        maxWidth: 1400,
        margin: '0 auto',
        // Safe-area-aware horizontal padding to align with page content (24px)
        paddingLeft: 'max(24px, env(safe-area-inset-left))',
        paddingRight: 'max(24px, env(safe-area-inset-right))',
        position: 'relative'
      }}>
        {/* Preload modal logo to eliminate perceived delay on first open */}
        <link rel="preload" as="image" href="/images/rmxballoonz.png" />
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
          <div
            onClick={onGoHome}
            style={{ cursor: 'pointer', display: 'flex', alignItems: 'center' }}
            aria-label="Go to gallery view"
          >
            <AgentAvatar name="Open Houses" size={40} />
          </div>
          <div style={{ cursor: 'pointer' }}>
            <div
              style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}
              onClick={onGoHome}
            >
              <h1 style={{ 
                fontFamily: FONT_STACK.join(','), 
                fontSize: '1.1rem',
                fontWeight: 600,
                lineHeight: '1.2',
                margin: 0
              }}>
                {headerTitle}
              </h1>
              {dataMode === 'demo' && (
                <span 
                  onClick={() => {
                    if (window.confirm('Return to main site?')) {
                      window.location.href = '/';
                    }
                    // Reset styles after click regardless of confirm result
                    setTimeout(() => {
                      const element = document.querySelector('[data-demo-pill]');
                      if (element) {
                        element.style.backgroundColor = '#0C2749';
                        element.style.transform = 'scale(1)';
                      }
                    }, 100);
                  }}
                  data-demo-pill
                  style={{
                    backgroundColor: '#0C2749',
                    color: 'white',
                    padding: '2px 6px',
                    borderRadius: '10px',
                    fontSize: '0.6rem',
                    fontWeight: 'bold',
                    letterSpacing: '0.5px',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease'
                  }}
                  onMouseOver={(e) => {
                    e.target.style.backgroundColor = '#0043ff';
                    e.target.style.transform = 'scale(1.05)';
                  }}
                  onMouseOut={(e) => {
                    e.target.style.backgroundColor = '#0C2749';
                    e.target.style.transform = 'scale(1)';
                  }}
                  onTouchStart={(e) => {
                    e.target.style.backgroundColor = '#0043ff';
                    e.target.style.transform = 'scale(1.05)';
                  }}
                  onTouchEnd={(e) => {
                    // Reset styles after touch end
                    setTimeout(() => {
                      e.target.style.backgroundColor = '#0C2749';
                      e.target.style.transform = 'scale(1)';
                    }, 150);
                  }}
                >
                  DEMO
                </span>
              )}
            </div>
            <div style={{ 
              fontSize: '0.8rem', 
              color: COLORS.primaryDarkBlue,
              lineHeight: '1.2',
              whiteSpace: 'nowrap'
            }}
            onClick={onGoHome}
            >
              REMAX Key Properties
            </div>
          </div>
        </div>
        {/* Mobile hamburger menu (replaces the mode-switch text link) */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
          <button
            ref={mobileMenuButtonRef}
            type="button"
            aria-label="Menu"
            aria-haspopup="menu"
            aria-expanded={isMobileMenuOpen ? 'true' : 'false'}
            onClick={() => setIsMobileMenuOpen((v) => !v)}
            style={{
              background: 'transparent',
              border: '1px solid rgba(0,0,0,0.12)',
              borderRadius: '10px',
              width: 40,
              height: 40,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              padding: 0
            }}
          >
            <FiMenu size={18} color="#111" />
          </button>

          {isMobileMenuOpen && mobileMenuAnchor && ReactDOM.createPortal(
            <div
              ref={mobileMenuRef}
              role="menu"
              aria-label="Mobile menu"
              style={{
                position: 'fixed',
                top: mobileMenuAnchor.top,
                right: mobileMenuAnchor.right,
                minWidth: 210,
                maxHeight: mobileMenuAnchor.maxHeight,
                overflowY: 'auto',
                background: 'rgba(255,255,255,0.92)',
                border: '1px solid rgba(0,0,0,0.10)',
                borderRadius: '14px',
                boxShadow: '0 12px 28px rgba(0,0,0,0.14)',
                backdropFilter: 'saturate(180%) blur(16px)',
                WebkitBackdropFilter: 'saturate(180%) blur(16px)',
                padding: '6px',
                // Above Mapbox controls/overlays, below full-screen modals.
                zIndex: 1900
              }}
              onClick={(e) => e.stopPropagation()}
            >
              {[
                { key: 'openhouses', label: 'Open Houses', icon: <FiCalendar size={16} />, value: 'openhouses' },
                { key: 'listings', label: 'All Listings', icon: <FiHome size={16} />, value: 'listings' },
              ].map((item) => {
                const active = dataMode === item.value;
                return (
                  <button
                    key={item.key}
                    type="button"
                    role="menuitem"
                    onClick={() => {
                      // Match prior behavior: switching modes always returns to gallery.
                      if (typeof onViewModeChange === 'function') onViewModeChange('gallery');
                      handleDataModeNavigate(item.value);
                      setIsMobileMenuOpen(false);
                    }}
                    style={{
                      width: '100%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      gap: '10px',
                      padding: '10px 10px',
                      borderRadius: '12px',
                      border: 'none',
                      // No persistent selection highlight; use the checkmark only.
                      background: 'transparent',
                      color: '#111',
                      cursor: 'pointer',
                      fontSize: '14px',
                      fontWeight: 600,
                      outline: 'none',
                      WebkitTapHighlightColor: 'transparent'
                    }}
                    onMouseDown={(e) => e.preventDefault()}
                    onMouseUp={(e) => { if (e.currentTarget) e.currentTarget.blur(); }}
                    onTouchEnd={(e) => { if (e.currentTarget) e.currentTarget.blur(); }}
                  >
                    <span style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                      <span style={{ width: 18, display: 'flex', justifyContent: 'center' }}>{item.icon}</span>
                      <span style={{ whiteSpace: 'nowrap' }}>{item.label}</span>
                    </span>
                    <span style={{ width: 18, display: 'flex', justifyContent: 'center', opacity: active ? 1 : 0 }}>
                      <FiCheck size={16} />
                    </span>
                  </button>
                );
              })}

              <div style={{ height: 1, background: 'rgba(0,0,0,0.08)', margin: '6px 8px' }} />

              <button
                type="button"
                role="menuitem"
                onClick={() => {
                  openContactModalFromMobileHeader('Mobile Menu');
                  setIsMobileMenuOpen(false);
                }}
                style={{
                  width: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: '10px',
                  padding: '10px 10px',
                  borderRadius: '12px',
                  border: 'none',
                  background: 'transparent',
                  color: '#111',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: 600,
                  outline: 'none',
                  WebkitTapHighlightColor: 'transparent'
                }}
                onMouseDown={(e) => e.preventDefault()}
                onMouseUp={(e) => { if (e.currentTarget) e.currentTarget.blur(); }}
                onTouchEnd={(e) => { if (e.currentTarget) e.currentTarget.blur(); }}
              >
                <span style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                  <span style={{ width: 18, display: 'flex', justifyContent: 'center' }}>
                    <FiPhone size={16} />
                  </span>
                  <span style={{ whiteSpace: 'nowrap' }}>Contact</span>
                </span>
                <span style={{ width: 18 }} />
              </button>
            </div>,
            document.body
          )}
        </div>
      </div>
    </header>
  );

  // Define desktop header JSX
  const desktopHeader = (
    <header style={{
      background: COLORS.white,
      borderBottom: `2px solid ${COLORS.primaryRed}`,
      padding: '1rem 0',
      position: 'relative',
      zIndex: 1000,
      minHeight: '80px',
      display: 'flex',
      alignItems: 'center'
    }}>
      <div style={{
        display: 'grid',
        gridTemplateColumns: '1fr auto 1fr',
        alignItems: 'center',
        gap: '2rem',
        width: '100%',
        maxWidth: 1400,
        margin: '0 auto',
        padding: '0 24px'
      }}>
        {/* Open Houses Info */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', justifySelf: 'start' }}>
          <button
            type="button"
            aria-label="Go to gallery view"
            onClick={onGoHome}
            style={{
              background: 'transparent',
              border: 'none',
              padding: 0,
              margin: 0,
              display: 'flex',
              alignItems: 'center',
              cursor: 'pointer',
              // Avoid the browser's default focus ring highlight on click/tap.
              outline: 'none',
              boxShadow: 'none',
              WebkitTapHighlightColor: 'transparent'
            }}
            onMouseDown={(e) => {
              // Prevent mouse click from leaving the button in a focused state (blue outline).
              e.preventDefault();
            }}
            onMouseUp={(e) => {
              if (e.currentTarget) e.currentTarget.blur();
            }}
            onTouchStart={(e) => {
              // Same idea for touch devices that emulate focus on tap.
              e.preventDefault();
            }}
            onTouchEnd={(e) => {
              if (e.currentTarget) e.currentTarget.blur();
            }}
          >
            <AgentAvatar name="Open Houses" size={48} />
          </button>
          <div style={{ cursor: 'pointer' }} onClick={onGoHome}>
            <div style={{ position: 'relative', display: 'inline-block' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <button
                  ref={desktopDataModeButtonRef}
                  type="button"
                  aria-label={canSwitchDataMode ? 'Switch between Open Houses and All Listings' : headerTitle}
                  aria-haspopup={canSwitchDataMode ? 'menu' : undefined}
                  aria-expanded={canSwitchDataMode ? (isDesktopDataModeMenuOpen ? 'true' : 'false') : undefined}
                  onClick={(e) => {
                    // Prevent "go home" click on the parent container.
                    e.preventDefault();
                    e.stopPropagation();
                    if (!canSwitchDataMode) return;
                    setIsDesktopDataModeMenuOpen((v) => !v);
                  }}
                  style={{
                    fontFamily: FONT_STACK.join(','),
                    fontSize: '2rem',
                    fontWeight: 600,
                    margin: 0,
                    // Keep the title text left-aligned with the subtitle beneath it.
                    // Only pad on top/right/bottom so we preserve the click target without shifting the text.
                    padding: canSwitchDataMode ? '4px 10px 4px 0' : 0,
                    borderRadius: canSwitchDataMode ? 12 : 0,
                    border: 'none',
                    background: 'transparent',
                    color: '#111',
                    cursor: canSwitchDataMode ? 'pointer' : 'default',
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: canSwitchDataMode ? 10 : 0,
                    lineHeight: 1.1,
                    WebkitTapHighlightColor: 'transparent',
                    // Avoid the browser's default focus ring highlight on click/tap.
                    outline: 'none',
                    boxShadow: 'none',
                    // Subtle "Apple-like" affordance without a background pill that exposes alignment.
                    opacity: canSwitchDataMode && (isDesktopTitleHovered || isDesktopDataModeMenuOpen) ? 0.92 : 1,
                  }}
                  onMouseDown={(e) => {
                    // Prevent mouse click from leaving the button in a focused state (blue outline).
                    e.preventDefault();
                  }}
                  onMouseUp={(e) => {
                    if (e.currentTarget) e.currentTarget.blur();
                  }}
                  onTouchStart={(e) => {
                    // Same idea for touch devices that emulate focus on tap.
                    e.preventDefault();
                  }}
                  onTouchEnd={(e) => {
                    if (e.currentTarget) e.currentTarget.blur();
                  }}
                  onMouseEnter={() => {
                    if (!canSwitchDataMode) return;
                    setIsDesktopTitleHovered(true);
                  }}
                  onMouseLeave={() => {
                    setIsDesktopTitleHovered(false);
                  }}
                >
                  <span>{headerTitle}</span>
                  {canSwitchDataMode && (
                    <FiChevronDown
                      size={18}
                      color={isDesktopTitleHovered || isDesktopDataModeMenuOpen ? '#444' : '#666'}
                      style={{ marginTop: 3 }}
                    />
                  )}
                </button>
              {dataMode === 'demo' && (
                <div
                  onMouseEnter={() => setIsDemoHovered(true)}
                  onMouseLeave={() => setIsDemoHovered(false)}
                  onClick={() => {
                    if (window.confirm('Return to main site?')) {
                      window.location.href = '/';
                    }
                  }}
                  style={{
                    backgroundColor: isDemoHovered ? '#dc3545' : '#0C2749',
                    color: 'white',
                    padding: '2px 8px',
                    borderRadius: '12px',
                    fontSize: '0.7rem',
                    fontWeight: 'bold',
                    letterSpacing: '0.5px',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px',
                    position: 'relative'
                  }}
                >
                  {isDemoHovered ? (
                    <>
                      <span>×</span>
                      <span>DEMO</span>
                    </>
                  ) : (
                    'DEMO'
                  )}
                </div>
              )}
            </div>

            <div style={{ color: COLORS.primaryDarkBlue, lineHeight: '1.2' }}>REMAX Key Properties</div>

            {canSwitchDataMode && isDesktopDataModeMenuOpen && (
              <div
                ref={desktopDataModeMenuRef}
                role="menu"
                aria-label="Data mode"
                style={{
                  position: 'absolute',
                  // Anchor below BOTH the title and subtitle so we don't overlap header copy.
                  top: 'calc(100% + 8px)',
                  left: 0,
                  minWidth: 220,
                  // Slightly more opaque than the mobile menu so header text doesn't ghost through.
                  background: 'rgba(255,255,255,0.98)',
                  border: '1px solid rgba(0,0,0,0.10)',
                  borderRadius: '14px',
                  boxShadow: '0 12px 28px rgba(0,0,0,0.14)',
                  backdropFilter: 'saturate(180%) blur(16px)',
                  WebkitBackdropFilter: 'saturate(180%) blur(16px)',
                  padding: '6px',
                  zIndex: 1900
                }}
                onClick={(e) => e.stopPropagation()}
              >
                {[
                  { key: 'openhouses', label: 'Open Houses', icon: <FiCalendar size={16} />, value: 'openhouses' },
                  { key: 'listings', label: 'All Listings', icon: <FiHome size={16} />, value: 'listings' },
                ].map((item) => {
                  const active = dataMode === item.value;
                  return (
                    <button
                      key={item.key}
                      type="button"
                      role="menuitem"
                      onClick={() => {
                        // Match prior behavior: switching modes always returns to gallery.
                        if (typeof onViewModeChange === 'function') onViewModeChange('gallery');
                        handleDataModeNavigate(item.value);
                        setIsDesktopDataModeMenuOpen(false);
                      }}
                      style={{
                        width: '100%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        gap: '10px',
                        padding: '10px 10px',
                        borderRadius: '12px',
                        border: 'none',
                        background: 'transparent',
                        color: '#111',
                        cursor: 'pointer',
                        fontSize: '14px',
                        fontWeight: 600,
                        outline: 'none',
                        WebkitTapHighlightColor: 'transparent'
                      }}
                      onMouseDown={(e) => e.preventDefault()}
                      onMouseUp={(e) => { if (e.currentTarget) e.currentTarget.blur(); }}
                    >
                      <span style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                        <span style={{ width: 18, display: 'flex', justifyContent: 'center' }}>{item.icon}</span>
                        <span style={{ whiteSpace: 'nowrap' }}>{item.label}</span>
                      </span>
                      <span style={{ width: 18, display: 'flex', justifyContent: 'center', opacity: active ? 1 : 0 }}>
                        <FiCheck size={16} />
                      </span>
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        </div>
        </div>

        {/* View Controls */}
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '6px' }}>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '1rem',
              marginTop: 0
            }}
          >
            {/* View Mode Toggle */}
            <div className="toggle-group">
              <button
                className={`toggle-button ${viewMode === 'gallery' ? 'active' : ''}`}
                onClick={() => onViewModeChange('gallery')}
              >
                <FiGrid /> Gallery
              </button>
              <button
                className={`toggle-button ${viewMode === 'map' ? 'active' : ''}`}
                onClick={() => onViewModeChange('map')}
              >
                <FiMap /> Map
              </button>
            </div>
          </div>
        </div>

        {/* Agent Contact Info */}
        <div style={{ textAlign: 'right', minWidth: 180, justifySelf: 'end' }}>
          <div style={{ fontWeight: 600 }}>{formatPhoneNumber(agent.phone)}</div>
          <div style={{ color: COLORS.primaryBlue }}>{agent.email}</div>
          <div style={{ fontSize: 14, color: '#888' }}>{agent.address}</div>
        </div>
      </div>
    </header>
  );

  return (
    <>
      <ContactModal 
        agentInfo={agent}
        show={showContactModal} 
        onClose={() => setShowContactModal(false)} 
        isMobile={isMobile} 
      />
      {isMobile ? mobileHeader : desktopHeader}
    </>
  );
}
